// ============================================================================
// FENÊTRES DU CALENDRIER DE PAIE : DATES RÉELLES DU MOIS
// ============================================================================
//
// Une fenêtre se règle en numéros de jour du mois de paie (jour 1 = date_debut
// du mois configuré), par exemple « du 24 au 26 ». Jusqu'au 19 septembre 2026,
// ces numéros s'appliquaient tels quels : un 24 qui tombait un dimanche
// raccourcissait la fenêtre, et une fenêtre d'un jour pouvait disparaître dans
// un week-end sans que rien ne le dise.
//
// Règle (décision de Daniel, 19 septembre 2026) : une borne qui tombe un samedi
// ou un dimanche passe au jour ouvré SUIVANT ; si ce jour sort du mois de paie,
// elle passe au jour ouvré PRÉCÉDENT. La fenêtre ne perd donc jamais un jour
// ouvré et reste dans son mois. Les jours fériés ne sont pas connus.
//
// COPIE CÔTÉ ÉCRAN de backend2/utils/fenetresPaie.js : toute modification de
// la règle se fait dans les deux fichiers. Le serveur reste seul juge ; cette
// copie sert à afficher les mêmes dates que celles qu'il applique.
// ============================================================================

const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août',
  'septembre', 'octobre', 'novembre', 'décembre'];

// Dates manipulées en UTC à partir de « AAAA-MM-JJ » : un Date local décale
// d'un jour selon le fuseau (CALENDRIER-DE-PAIE.md).
// Une date peut arriver en texte (« 2026-10-01 », « 2026-10-01T00:00:00 ») ou
// en objet Date (colonne DATE lue par mysql2, créée à minuit LOCAL) : on
// reprend alors ses composantes locales, jamais son heure UTC.
const versIso = (valeur) => {
  if (valeur instanceof Date) {
    return [
      valeur.getFullYear(),
      String(valeur.getMonth() + 1).padStart(2, '0'),
      String(valeur.getDate()).padStart(2, '0')
    ].join('-');
  }
  return String(valeur || '').slice(0, 10);
};
const versUtc = (valeur) => {
  const [a, m, j] = versIso(valeur).split('-').map(Number);
  return Date.UTC(a, m - 1, j);
};
const ajouterJours = (iso, n) => new Date(versUtc(iso) + n * 86400000).toISOString().slice(0, 10);
const jourSemaine = (iso) => new Date(versUtc(iso)).getUTCDay();
const estWeekEnd = (iso) => [0, 6].includes(jourSemaine(iso));

export const formaterDateLongue = (iso) => {
  const d = new Date(versUtc(iso));
  return `${JOURS[d.getUTCDay()]} ${d.getUTCDate()} ${MOIS[d.getUTCMonth()]}`;
};

/** Nombre de jours du mois de paie, bornes comprises. */
export const nombreJours = (dateDebut, dateFin) => Math.round((versUtc(dateFin) - versUtc(dateDebut)) / 86400000) + 1;

/**
 * Rang effectif d'une borne : ramenée dans le mois, puis décalée hors du
 * week-end (suivant d'abord, précédent si le suivant sort du mois).
 */
export function ajusterRang(dateDebutMois, nbJours, rang) {
  const initial = Math.min(Math.max(Number(rang) || 1, 1), nbJours);
  let effectif = initial;
  const dateDe = (r) => ajouterJours(dateDebutMois, r - 1);

  if (estWeekEnd(dateDe(effectif))) {
    let suivant = effectif;
    while (suivant <= nbJours && estWeekEnd(dateDe(suivant))) suivant += 1;
    if (suivant <= nbJours) {
      effectif = suivant;
    } else {
      let precedent = effectif;
      while (precedent >= 1 && estWeekEnd(dateDe(precedent))) precedent -= 1;
      if (precedent >= 1) effectif = precedent;
    }
  }

  return {
    rang_initial: initial,
    rang: effectif,
    date_initiale: dateDe(initial),
    date: dateDe(effectif),
    decale: effectif !== initial
  };
}

/**
 * Fenêtre effective pour un mois de paie donné.
 * Renvoie null si la fenêtre n'est pas configurée ou le mois inconnu.
 */
export function fenetreEffective({ dateDebutMois, dateFinMois, debut, fin }) {
  const borneDebut = Number(debut || 0);
  const borneFin = Number(fin || 0);
  if (!borneDebut || !borneFin || !dateDebutMois || !dateFinMois) return null;

  dateDebutMois = versIso(dateDebutMois);
  dateFinMois = versIso(dateFinMois);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateDebutMois) || !/^\d{4}-\d{2}-\d{2}$/.test(dateFinMois)) return null;
  const nbJours = nombreJours(dateDebutMois, dateFinMois);
  const d = ajusterRang(dateDebutMois, nbJours, borneDebut);
  const f = ajusterRang(dateDebutMois, nbJours, borneFin);
  // Deux bornes qui glissent dans le même week-end peuvent se croiser : la
  // fenêtre se réduit alors au jour ouvré retenu pour son début.
  const rangFin = borneDebut <= borneFin && f.rang < d.rang ? d.rang : f.rang;

  return {
    reglage_debut: borneDebut,
    reglage_fin: borneFin,
    rang_debut: d.rang,
    rang_fin: rangFin,
    date_debut: d.date,
    date_fin: ajouterJours(dateDebutMois, rangFin - 1),
    // Une fenêtre réglée « du 28 au 5 » enjambe la fin du mois.
    enjambe_fin_de_mois: borneDebut > borneFin,
    decalages: [
      d.decale ? { borne: 'debut', de: d.date_initiale, vers: d.date } : null,
      f.decale ? { borne: 'fin', de: f.date_initiale, vers: ajouterJours(dateDebutMois, rangFin - 1) } : null,
      // Borne réglée au-delà du dernier jour (un 31 en février) : on le dit,
      // avec le vrai dernier jour du mois de paie.
      borneFin > nbJours ? { borne: 'fin_de_mois', de: null, vers: ajouterJours(dateDebutMois, nbJours - 1) } : null
    ].filter(Boolean)
  };
}

// ----------------------------------------------------------------------------
// Les cinq étapes du calendrier
// ----------------------------------------------------------------------------
// `bloquante` : le serveur refuse l'opération hors de ses dates, pendant le
// mois de paie en cours. Les autres étapes sont indicatives.
export const ETAPES_PAIE = [
  {
    cle: 'avances', libelle: 'Avances', debut: 'avance_jour_debut', fin: 'avance_jour_fin', bloquante: true,
    regle: "Hors de ces dates, aucune nouvelle avance ne peut être créée. La modification et l'annulation restent possibles pour corriger une erreur."
  },
  {
    cle: 'etat_salaires', libelle: 'État des salaires', debut: 'etat_salaires_jour_debut', fin: 'etat_salaires_jour_fin', bloquante: false,
    regle: "Indicatif : l'état se consulte et se prépare à tout moment."
  },
  {
    cle: 'demande_dotation', libelle: 'Demande de dotation', debut: 'demande_dotation_jour_debut', fin: 'demande_dotation_jour_fin', bloquante: true,
    regle: 'Hors de ces dates, la demande de dotation salaires est refusée.'
  },
  {
    cle: 'validation_centrale', libelle: 'Validation centrale', debut: 'validation_centrale_jour_debut', fin: 'validation_centrale_jour_fin', bloquante: false,
    regle: 'Indicatif : la caisse centrale peut accepter la dotation à tout moment.'
  },
  {
    cle: 'paiement_salaires', libelle: 'Paiement', debut: 'paiement_salaires_jour_debut', fin: 'paiement_salaires_jour_fin', bloquante: true,
    regle: 'Hors de ces dates, le paiement des salaires est refusé.'
  }
];

const texteFenetre = (f) => {
  if (!f) return 'Non configurée';
  if (f.date_debut === f.date_fin) return `le ${formaterDateLongue(f.date_debut)}`;
  return `du ${formaterDateLongue(f.date_debut)} au ${formaterDateLongue(f.date_fin)}`;
};

const texteDecalage = (decalage) => {
  if (decalage.borne === 'fin_de_mois') return `Le mois de paie s'arrête le ${formaterDateLongue(decalage.vers)}`;
  const quoi = decalage.borne === 'debut' ? 'Ouverture' : 'Fermeture';
  return `${quoi} déplacée du ${formaterDateLongue(decalage.de)} (week-end) au ${formaterDateLongue(decalage.vers)}`;
};

/**
 * Le calendrier d'un mois de paie : dates réelles, décalages, et ce qui est
 * bloqué. `mois` porte date_debut / date_fin ; `estCourant` dit si les
 * fenêtres s'appliquent aujourd'hui.
 */
export function calendrierDuMois(params = {}, mois = {}, { estCourant = false, ouvertures = {}, demandesEnAttente = {} } = {}) {
  const etapes = ETAPES_PAIE.map((etape) => {
    const f = fenetreEffective({
      dateDebutMois: mois.date_debut,
      dateFinMois: mois.date_fin,
      debut: params[etape.debut],
      fin: params[etape.fin]
    });
    return {
      cle: etape.cle,
      libelle: etape.libelle,
      bloquante: etape.bloquante,
      applique: etape.bloquante && estCourant && Boolean(f),
      reglage: params[etape.debut] && params[etape.fin]
        ? `jours ${params[etape.debut]} à ${params[etape.fin]} du mois de paie`
        : null,
      dates: texteFenetre(f),
      date_debut: f?.date_debut || null,
      date_fin: f?.date_fin || null,
      decalages: (f?.decalages || []).map(texteDecalage),
      regle: etape.regle,
      // Ouverture exceptionnelle active aujourd'hui (OuvertureFenetrePaieService).
      ouverture: ouvertures[etape.cle] ? {
        id: ouvertures[etape.cle].id,
        jusqu_au: formaterDateLongue(ouvertures[etape.cle].date_fin),
        accordee_par: ouvertures[etape.cle].validateur_nom || null,
        auto_validee: Number(ouvertures[etape.cle].auto_validee) === 1
      } : null,
      demande_en_attente: demandesEnAttente[etape.cle] || null
    };
  });

  return {
    est_courant: estCourant,
    etapes,
    // Compatibilité : les textes simples, par clé (export PDF de l'état).
    textes: Object.fromEntries(etapes.map((e) => [e.cle, e.dates])),
    message: estCourant
      ? "Pendant le mois de paie en cours, le système bloque les opérations en dehors des dates des étapes « Avances », « Demande de dotation » et « Paiement ». Une date qui tombe un week-end est reportée au jour ouvré suivant. Pour une opération hors de ces dates, une ouverture exceptionnelle peut être demandée : elle est validée au Centre de Support."
      : "Ce mois n'est pas le mois de paie en cours : ses dates ne bloquent rien aujourd'hui. Seuls les autres contrôles s'appliquent (pointages, dotation acceptée, état validé)."
  };
}
